<template>
  <v-app>
    <shopify-savebar />
    <core-top-menu
      v-if="!hideMenu && showCoreMenu"
      :class="showCoreViewInMobile ? 'showCoreView' : 'desktop'"
    />
    <not-mobile-optimized
      :class="showCoreViewInMobile ? 'none' : 'mobile'"
      @continue="continueToCoreView"
      class="z-100"
    />

    <div
      v-if="loadingCount || onlyLoading"
      style="height: 80vh; display: flex; justify-content: center; align-items: center"
    >
      <v-progress-circular
        :size="120"
        :width="12"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>

    <div :class="{ none: loadingCount || onlyLoading }">
      <widget-installation-error-bar
        class="stickyTrialBar"
        v-if="!accountInfo.shownWidgetInstallationErrorBar"
      />
      <trial-period-bar
        v-if="showTrial"
        class="stickyTrialBar"
        :class="[trialWithoutMenu ? 'trialBarNoMenu' : '']"
      />
      <saver-bar class="savebar" />
      <toast />
      <core-view :class="showCoreViewInMobile ? 'showCoreView' : 'desktop'" />
    </div>
  </v-app>
</template>
<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import NotMobileOptimized from '@/components/core/NotMobileOptimized';
import SaverBar from '@/components/core/SaverBar';
import TrialPeriodBar from '@/components/core/TrialPeriodBar';
import WidgetInstallationErrorBar from '@/components/core/WidgetInstallationErrorBar';
import View from '@/components/core/View';
const TopMenu = () => import('@/components/core/TopMenu');
import Toast from '@/components/core/Toast';
import useFreshchat from '@/helpers/freshchat';
import ShopifySavebar from '@/components/common/ShopifySavebar.vue';

export default {
  name: 'App',
  props: ['hideMenu'],
  components: {
    ShopifySavebar,
    NotMobileOptimized,
    SaverBar,
    TrialPeriodBar,
    WidgetInstallationErrorBar,
    CoreView: View,
    CoreTopMenu: TopMenu,
    Toast,
  },
  data() {
    return {
      extensionInterval: null,
      showCoreViewInMobile: false,
      onlyLoading: false,
    };
  },
  created() {
    // this.setShopId(null);
    if (window.location.pathname === '/dashboard/auth') {
      // TODO: Handle this better
      return;
    }

    if (window.location.hash.includes('loading=true')) {
      this.onlyLoading = true;
      setTimeout(() => {
        if (!window.location.hash.includes('loading=true')) {
          this.onlyLoading = false;
        }
      }, 2500);
    }

    if (!this.getAuthenticated()) {
      this.onlyLoading = false;
      this.$router.push({ name: 'Auth' });
    }

    const {
      subscription_activated,
      plan_id,
      subscription_amount,
      welcome_screen,
      install_complete,
    } = this.getUrlParams();
    if (subscription_activated && !install_complete) {
      this.$ga.event({
        eventCategory: 'Tier Upgrade',
        eventAction: 'Purchase',
        eventLabel: plan_id,
        eventValue: parseInt(subscription_amount),
      });
      this.$router.push({ name: 'Overview', hash: window.location.hash });
    }

    if (welcome_screen) {
      this.$hj('tagRecording', ['New User']);
      this.$router.push({
        name: 'Welcome',
        params: { step: parseInt(welcome_screen) },
      });
    }
  },
  mounted() {
    this.setManualHistory([]);
    setTimeout(() => {
      this.updateLoadingCount(-1);
    }, 100);

    if (!this.extensionInterval) {
      this.extensionInterval = setInterval(this.verifyChromeExtension, 4000);
    }
  },
  beforeDestroy() {
    this.hideToastMessage();
    if (this.extensionInterval) {
      clearInterval(this.extensionInterval);
    }
  },
  computed: {
    ...mapState(['accountInfo', 'loadingCount']),
    routePath() {
      const { path } = this.$route;
      const routePath =
        path && path.length > 1
          ? path
          : // acquire view path before router is ready to prevent layout shifts
            window.location.href
              .replace(window.location.origin, '')
              .replace(/^\/(#|dashboard)/i, '')
              .toLowerCase();

      return routePath;
    },
    showTrial() {
      return (
        this.accountInfo.onboardingOffer &&
        !['/welcome/:step*', '/auth', '/first-steps', '/plan-pick'].some(p =>
          this.routePath.includes(p)
        )
      );
    },
    trialWithoutMenu() {
      return [
        '/install-wizard',
        '/pick-trial-plan',
        '/request-wizard',
        '/request-email-template',
        '/coupon-email-template',
        '/photo-email-template',
        '/thank-you-email-template',
        '/plans-billings',
      ].some(p => this.routePath.includes(p));
    },
    showCoreMenu() {
      return ![
        '/welcome',
        '/pick-trial-plan',
        '/first-steps',
        '/auth',
        '/plan-pick',
        '/install-wizard',
        // '/request-email-template',
        // '/coupon-email-template',
        // '/photo-email-template',
        // '/sms-templates',
        // '/thank-you-email-template',
        '/request-wizard',
      ].some(p => this.routePath.includes(p));
    },
  },
  methods: {
    ...mapMutations(['updateLoadingCount']),
    ...mapActions(['verifyChromeExtension']),
    ...mapGetters('auth', ['getAuthenticated']),
    ...mapActions(['trackAnalytics', 'hideToastMessage']),
    ...mapMutations('app', ['setManualHistory']),
    getUrlParams() {
      const params = {};
      //820 notes: unused variable removed
      window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
        if (key === 'is_editing') {
          //convert "false"/"true" to boolean
          params[key] = value === 'true';
        } else {
          params[key] = value;
        }
      });
      return params;
    },
    continueToCoreView(show) {
      this.showCoreViewInMobile = show;
    },
  },
  watch: {
    $route() {
      if (this.accountInfo.shopId) {
        window.analytics.page(this.$route.name, {
          shopId: this.accountInfo.shopId,
        });
      }
    },

    accountinfoLoading: {
      handler(val) {
        if (!val) {
          setTimeout(() => {
            const { showFreshchat } = useFreshchat();
            showFreshchat();
          }, 500);

          window.analytics.identify(this.accountInfo.shopId, {
            name: this.accountInfo.shopName,
            email: this.accountInfo.shopOwnerEmail,
            shopId: this.accountInfo.shopId,
          });
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
@import '~vue-toasted/dist/vue-toasted.min.css';
@import '@/styles/polaris/common';

.none {
  display: none;
}

@media (min-width: 1024px) {
  .mobile {
    display: none;
  }
  .desktop {
    display: block;
  }
}

@media (max-width: 1023px) {
  .mobile {
    display: block;
  }
  .desktop {
    display: none;
  }
}

@media (max-width: 1023px) {
  .showCoreView {
    display: block;
  }
}

.toasted.bubble {
  font-weight: 300;
  font-family: sans-serif;
}

.trialBarNoMenu {
  top: 0 !important;
}

.savebar {
  position: fixed;
  top: 3.2rem;
  align-self: flex-start;
  z-index: 31;
  max-width: 100%;
  width: 100%;
}

.stickyTrialBar {
  position: sticky;
  top: 3.2rem;
  align-self: flex-start;
  z-index: 30;
  max-width: 100%;
  width: 100%;
}

.opinew-onboarding .fc-widget-normal {
  bottom: 65px !important;
  right: 35px !important;
}
</style>
